import auth from "./auth";
import utils from "../utils";
import featureFlags from "../featureFlags";
import throttle from "lodash/throttle";
import user from "../user";
import reactiveWebStorage from "../reactiveWebStorage";
import { isExcludedRoute } from './loginExcludedRoutes';

reactiveWebStorage.registerKey({
  key: "inactivityLogoutMinutes",
  defaultValue: 0,
  storageType: "localStorage",
  valueType: "number",
});

reactiveWebStorage.registerKey({
  key: "inactivityLogoutMinutesGuest",
  defaultValue: 0,
  storageType: "localStorage",
  valueType: "number",
});

reactiveWebStorage.registerKey({
  key: "previousActivity",
  storageType: "localStorage",
  valueType: "datetime",
});

export function cleanStorage() {
  reactiveWebStorage.del('inactivityLogoutMinutes');
  reactiveWebStorage.del('inactivityLogoutMinutesGuest');
  reactiveWebStorage.del('previousActivity');
}

let nextCheck;
let inactivityLogoutMinutes;
const checkIntervalMsec = 1000;

function isGuest() {
  return user.isGuest();
}

async function autoLogout() {
  // we dont want to logout if the user is on a login excluded route
  if (isExcludedRoute()) {
    return;
  }

  auth.log("auth autoLogout");
  await auth.logout({ autoLogout: true, isGuest: isGuest() });
}

function calcNextCheck(lastActivity = getLastActivity()) {
  if (!inactivityLogoutMinutes) return undefined;
  if (!lastActivity) return undefined;
  return new Date(lastActivity.getTime() + inactivityLogoutMinutes * 60 * 1000);
}

async function checkInactivity() {
  //auth.log(
  //  `auth checkInactivity ${inactivityLogoutMinutes} | ${nextCheck} ||| ${new Date()}`
  //);
  if (!nextCheck || new Date() < nextCheck) return;


  auth.log(
    `inactivityLogoutMinutes: ${inactivityLogoutMinutes}`
  );

  if (!inactivityLogoutMinutes) return;

  const lastActivity = getLastActivity();
  if (!lastActivity) return;

  const now = new Date();
  const diff = now - lastActivity;
  const diffMinutes = Math.floor(diff / 1000 / 60);

  auth.log("auth diffMinutes", diffMinutes);

  if (diffMinutes >= inactivityLogoutMinutes) {
    auth.log("auth InactivityLogoutMinutes: ", inactivityLogoutMinutes);
    auth.log("auth diffMinutes: ", diffMinutes);
    auth.log("auth Logging out due to inactivity");

    await autoLogout();
  } else {
    nextCheck = calcNextCheck(lastActivity);
  }
}

function registerInterval(handler, intervalMsec) {
  const intervalId = setInterval(handler, intervalMsec);
  return {
    unregister() {
      clearInterval(intervalId);
    },
  };
}

async function onSessionTimeout({ source }) {
  // we dont want to logout if the user is on a login excluded route
  if (isExcludedRoute()) {
    return;
  }

  auth.log(
    `auth Received integrify-portal:sessionTimeout from ${source}. Logging out...`
  );
  await autoLogout();
}

function onInactivityLogoutMinutesUpdate(value) {
  if (isGuest()) return;

  inactivityLogoutMinutes = value;
  nextCheck = calcNextCheck();
}

function onInactivityLogoutMinutesGuestUpdate(value) {
  if (!isGuest()) return;

  inactivityLogoutMinutes = value;
  nextCheck = calcNextCheck();
}

function onIsGuestUpdate(isGuest) {
  auth.log("onIsGuestUpdate", isGuest);

  // onInactivityLogoutMinutesUpdate depends on isGuest, so when it's updated, need to call it again
  onInactivityLogoutMinutesUpdate(
    reactiveWebStorage.get("inactivityLogoutMinutes")
  );

  // onInactivityLogoutMinutesGuestUpdate depends on isGuest, so when it's updated, need to call it again
  onInactivityLogoutMinutesGuestUpdate(
    reactiveWebStorage.get("inactivityLogoutMinutesGuest")
  );
}

function onLastActivityUpdate(lastActivity) {
  nextCheck = calcNextCheck(lastActivity);
}

export function setInactivityLogoutMinutes(newInactivityLogoutMinutes) {
  reactiveWebStorage.set("inactivityLogoutMinutes", newInactivityLogoutMinutes);
}

export function setInactivityLogoutMinutesGuest(
  newInactivityLogoutMinutesGuest
) {
  reactiveWebStorage.set(
    "inactivityLogoutMinutesGuest",
    newInactivityLogoutMinutesGuest
  );
}

/**
 * * @returns {Date | undefined}
 */
function getLastActivity() {
  return reactiveWebStorage.get("previousActivity");
}

export const updateLastActivity = throttle(function updateLastActivity() {
  reactiveWebStorage.set("previousActivity", new Date());
}, checkIntervalMsec);

function registerUserActivityTracker() {
  const excluded = new Set(["storage"]);

  // originated from https://stackoverflow.com/a/48388878/997994
  const events = Object.keys(window)
    .filter((key) => /^on/.test(key))
    .map((key) => key.slice(2))
    .filter((key) => !excluded.has(key));

  function onEvent(e) {
    if (e.isTrusted) {
      // The isTrusted read-only property of the Event interface is a boolean
      // that is true when the event was generated by a user action, and false
      // when the event was created or modified by a script or dispatched via dispatchEvent.
      updateLastActivity();
    }
  }

  for (const event of events) {
    window.addEventListener(event, onEvent);
  }

  return {
    unregister() {
      for (const event of events) {
        window.addEventListener(event, onEvent);
      }
    },
  };
}

export async function register() {
  if (utils.isInIFRAME()) return { unregister() {} };

  let unregisterOnSessionTimeout;

  let unregisterReactiveWebStorageUpdates;
  let unregisterIsGuestUpdate;

  let unregisterCheckInactivity;
  let unregisterUserActivityTracker;

  if (parent.portalGlobalEventBus) {
    unregisterOnSessionTimeout = parent.portalGlobalEventBus.$on(
      "integrify-portal:sessionTimeout",
      onSessionTimeout
    ).unregister;

    unregisterReactiveWebStorageUpdates = reactiveWebStorage.onUpdates({
      inactivityLogoutMinutes: onInactivityLogoutMinutesUpdate,
      inactivityLogoutMinutesGuest: onInactivityLogoutMinutesGuestUpdate,
      previousActivity: onLastActivityUpdate,
    }).unregister;
    unregisterIsGuestUpdate = user.onIsGuestUpdate(onIsGuestUpdate).unregister;
  }

  unregisterCheckInactivity = registerInterval(
    checkInactivity,
    checkIntervalMsec
  ).unregister;
  unregisterUserActivityTracker = registerUserActivityTracker().unregister;

  return {
    unregister() {
      unregisterOnSessionTimeout?.();
      unregisterCheckInactivity?.();
      unregisterUserActivityTracker?.();
      unregisterReactiveWebStorageUpdates?.();
      unregisterIsGuestUpdate?.();
    },
  };
}
